// Bug reports and browser error logging.
//  - installErrorCapture(): records window errors and unhandled rejections, keeps the last few for bug reports, and sends
//    deduplicated batches to POST /api/clienterror (a few seconds apart, ~20 entries per page load, beacon on hide).
//  - openBugForm(): a DOM form (not the HUD canvas, never pauses the world) that posts to POST /api/report.
// Nothing here may throw into the game: every entry point is wrapped.
import { BUILD } from './net';

const KEEP = 20, MAX_SENT = 20, FLUSH_MS = 5000;
export interface LoggedError { message: string; stack: string; source: string; line: number }
interface Pending extends LoggedError { n: number }

/** who is playing, set by main.ts: shown in the form and attached to the report */
export const fbContext = { room: '', charId: '', charName: '' };

const recent: LoggedError[] = [];
const pending = new Map<string, Pending>();
let sent = 0, timer = 0;

const cap = (s: unknown, n: number) => String(s ?? '').slice(0, n);
const firstFrame = (e: LoggedError) => e.stack.split('\n').map(l => l.trim()).find(l => /^at\s|@/.test(l)) ?? `${e.source}:${e.line}`;

function record(message: unknown, stack: unknown, source: unknown, line: unknown) {
  try {
    const e: LoggedError = { message: cap(message, 300), stack: cap(stack, 2000), source: cap(source, 200), line: Number(line) || 0 };
    if (!e.message) return;
    recent.push(e); if (recent.length > KEEP) recent.shift();
    const key = `${e.message}\n${firstFrame(e)}`;
    const p = pending.get(key);
    if (p) p.n++; else if (sent + pending.size < MAX_SENT) pending.set(key, { ...e, n: 1 });
    if (!timer && pending.size) timer = window.setTimeout(() => flush(false), FLUSH_MS);
  } catch { /* the reporter must never throw */ }
}

function flush(beacon: boolean) {
  try {
    window.clearTimeout(timer); timer = 0;
    if (!pending.size) return;
    const errors = [...pending.values()]; pending.clear(); sent += errors.length;
    const body = JSON.stringify({ errors, build: BUILD, ua: navigator.userAgent, inRoom: !!fbContext.room });
    if (beacon && navigator.sendBeacon) { navigator.sendBeacon('/api/clienterror', new Blob([body], { type: 'application/json' })); return; }
    void fetch('/api/clienterror', { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* ignore */ }
}

export function installErrorCapture() {
  addEventListener('error', ev => record(ev.message || ev.error?.message, ev.error?.stack, ev.filename, ev.lineno));
  addEventListener('unhandledrejection', ev => {
    const r = ev.reason;
    record(r instanceof Error ? r.message : `Unhandled rejection: ${typeof r === 'string' ? r : JSON.stringify(r) ?? String(r)}`, r instanceof Error ? r.stack : '', '', 0);
  });
  addEventListener('pagehide', () => flush(true));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(true); });
}

const MAX_REPORT_BYTES = 15000; // the server refuses bodies over 16 KB

let formEl: HTMLElement | null = null;
export function openBugForm() {
  try {
    if (formEl) return;
    const screen = `${innerWidth}x${innerHeight}`;
    const errs = recent.slice(-KEEP);
    const root = document.createElement('div'); formEl = root;
    root.id = 'bugform'; root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-label', 'Report a bug');
    root.innerHTML = `<form class="bf-card" novalidate>
      <h2>Report a bug</h2>
      <p class="bf-sub">Tell us what went wrong. The game keeps running while this is open.</p>
      <label>What happened? <b class="bf-req">(required)</b><textarea name="description" maxlength="2000" rows="5" required></textarea></label>
      <label>What were you doing? <span class="bf-opt">(optional)</span><textarea name="doing" maxlength="1000" rows="3"></textarea></label>
      <label>Contact <span class="bf-opt">(optional: Discord name or email, if you want an answer)</span><input name="contact" maxlength="100" autocomplete="off"></label>
      <p class="bf-fine"></p>
      <div class="bf-msg" role="status"></div>
      <div class="bf-bar"><button type="submit" class="bf-send">Send report</button><button type="button" class="bf-cancel">Cancel</button></div>
    </form>`;
    const form = root.querySelector('form') as HTMLFormElement;
    const fine = root.querySelector('.bf-fine') as HTMLElement, msg = root.querySelector('.bf-msg') as HTMLElement;
    const sendBtn = root.querySelector('.bf-send') as HTMLButtonElement, cancelBtn = root.querySelector('.bf-cancel') as HTMLButtonElement;
    fine.textContent = `Sent with your report: build ${BUILD || 'unknown'} · ${navigator.userAgent} · screen ${screen} · ${fbContext.room ? `room ${fbContext.room}` : 'not in a room'}`
      + ` · ${fbContext.charName ? `character ${fbContext.charName} (${fbContext.charId})` : 'no character yet'} · ${errs.length} recent error${errs.length === 1 ? '' : 's'}. No screenshot, no address.`;

    const close = () => { root.remove(); formEl = null; dispatchEvent(new Event('blur')); }; // blur releases any key held when it opened
    // Keys typed here are not game input (movement, menus, chat shortcuts).
    for (const t of ['keydown', 'keyup', 'keypress']) root.addEventListener(t, e => { e.stopPropagation(); if (t === 'keydown' && (e as KeyboardEvent).key === 'Escape') close(); });
    cancelBtn.onclick = close;

    form.onsubmit = async ev => {
      ev.preventDefault();
      const val = (n: string) => (form.elements.namedItem(n) as HTMLInputElement).value.trim();
      const description = val('description');
      msg.className = 'bf-msg';
      if (!description) { msg.textContent = 'Please describe what went wrong.'; msg.classList.add('bad'); (form.elements.namedItem('description') as HTMLElement).focus(); return; }
      const attach = errs.map(e => ({ ...e, stack: e.stack.slice(0, 600) }));
      const make = () => JSON.stringify({ description, doing: val('doing'), contact: val('contact'), build: BUILD, ua: navigator.userAgent, screen,
        room: fbContext.room, charId: fbContext.charId, charName: fbContext.charName, errors: attach });
      let body = make(); while (body.length > MAX_REPORT_BYTES && attach.length) { attach.shift(); body = make(); }
      sendBtn.disabled = true; msg.textContent = 'Sending...';
      try {
        const r = await fetch('/api/report', { method: 'POST', headers: { 'content-type': 'application/json' }, body });
        if (!r.ok) { const j = await r.json().catch(() => null); throw new Error(j?.error ?? `server answered ${r.status}`); }
        form.innerHTML = `<h2>Thank you</h2><p class="bf-sub">Your report was sent.</p><div class="bf-bar"><button type="button" class="bf-cancel">Close</button></div>`;
        const done = form.querySelector('.bf-cancel') as HTMLButtonElement; done.onclick = close; done.focus();
      } catch (e) {
        sendBtn.disabled = false; msg.classList.add('bad');
        msg.textContent = `Could not send: ${(e as Error).message}. Your text is still here; try again in a moment.`;
      }
    };
    document.body.appendChild(root);
    dispatchEvent(new Event('blur'));
    (form.elements.namedItem('description') as HTMLElement).focus();
  } catch (e) { formEl = null; console.warn('bug form', e); }
}
